"use client";

import { supabase } from "@/lib/supabase";
import type { DbArtifact } from "@/types/db";
import { useCallback, useEffect, useState } from "react";

// Columns the list consumers (home search dialog, artifacts panel, project
// attach dialog) actually render — deliberately excludes `html_content`,
// which is only needed when opening a single artifact (/artifacts/[id]).
const ARTIFACT_LIST_COLUMNS = "id, user_id, team_id, name, description, created_at, sharing";
const ARTIFACT_LIST_LIMIT = 200;

export type ArtifactListItem = Pick<
  DbArtifact,
  "id" | "user_id" | "team_id" | "name" | "description" | "created_at" | "sharing"
> & { project?: { id: string; name: string; canManage?: boolean } | null };

export type UseArtifactsOptions = {
  /** Skip the list fetch — for consumers that only need the mutation helpers. */
  skipFetch?: boolean;
  /** Include project artifacts and their visible project names in the library. */
  includeProjects?: boolean;
};

export type UseArtifactsReturn = {
  artifacts: ArtifactListItem[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  createArtifact: (params: {
    name: string;
    description?: string | null;
    htmlContent: string;
    teamId: string;
    sourceChatId?: string | null;
    originProjectId?: string | null;
  }) => Promise<string | null>;
  updateArtifact: (
    artifactId: string,
    fields: Partial<Pick<DbArtifact, "name" | "description" | "sharing" | "team_id" | "registers_access_granted">>
  ) => Promise<boolean>;
  deleteArtifact: (artifactId: string) => Promise<boolean>;
};

export function useArtifacts(options?: UseArtifactsOptions): UseArtifactsReturn {
  const includeProjects = options?.includeProjects ?? false;
  const [error, setError] = useState<string | null>(null);
  const skipFetch = options?.skipFetch ?? false;
  const [artifacts, setArtifacts] = useState<ArtifactListItem[]>([]);
  const [loading, setLoading] = useState(!skipFetch);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (skipFetch) return;

    let cancelled = false;

    void (async () => {
      setLoading(true);

      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;

      if (cancelled) return;
      if (!user) {
        setArtifacts([]);
        setLoading(false);
        return;
      }

      setError(null);
      try {
        const all: ArtifactListItem[] = [];
        // Read every page rather than silently truncating the library at 200.
        for (let offset = 0; ; offset += ARTIFACT_LIST_LIMIT) {
          const { data, error: fetchError } = await supabase
            .from("artifacts")
            .select(ARTIFACT_LIST_COLUMNS)
            .order("created_at", { ascending: false })
            .order("id")
            .range(offset, offset + ARTIFACT_LIST_LIMIT - 1);
          if (cancelled) return;
          if (fetchError) throw fetchError;
          const batch = (data ?? []) as ArtifactListItem[];
          if (!batch.length) break;
          const { data: links, error: linkError } = await supabase
            .from("project_resources")
            .select("resource_id, project_id, projects(id, name, owner_id, project_members(user_id, role))")
            .eq("resource_type", "artifact")
            .in("resource_id", batch.map((artifact) => artifact.id));
          if (cancelled) return;
          if (linkError) throw linkError;
          const projectByArtifact = new Map(
            ((links ?? []) as unknown as Array<{
              resource_id: string;
              project_id: string;
              projects: { id: string; name: string; owner_id: string; project_members: Array<{ user_id: string; role: string }> } | null;
            }>).map((link) => [link.resource_id, {
              id: link.project_id,
              name: link.projects?.name ?? "Project",
              canManage: link.projects?.owner_id === user.id || Boolean(link.projects?.project_members.some((member) => member.user_id === user.id && member.role === "can_edit")),
            }])
          );
          all.push(...batch
            .filter((artifact) => includeProjects || !projectByArtifact.has(artifact.id))
            .map((artifact) => ({ ...artifact, project: projectByArtifact.get(artifact.id) ?? null })));
          if (batch.length < ARTIFACT_LIST_LIMIT) break;
        }
        setArtifacts(all);
      } catch (fetchError) {
        if (cancelled) return;
        console.error("[useArtifacts] fetch:", fetchError);
        setArtifacts([]);
        setError("Failed to load artifacts. Please try again.");
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshKey, skipFetch, includeProjects]);

  const createArtifact = useCallback(
    async ({
      name,
      description,
      htmlContent,
      teamId,
      sourceChatId,
      originProjectId,
    }: {
      name: string;
      description?: string | null;
      htmlContent: string;
      teamId: string;
      sourceChatId?: string | null;
      originProjectId?: string | null;
    }): Promise<string | null> => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return null;

      const { data, error } = await supabase
        .from("artifacts")
        .insert({
          user_id: user.id,
          team_id: teamId,
          name,
          description: description ?? null,
          html_content: htmlContent,
          sharing: "private",
          source_chat_id: sourceChatId ?? null,
          origin_project_id: originProjectId ?? null,
        })
        .select("id")
        .single();

      if (error) {
        console.error("[useArtifacts] create:", error);
        return null;
      }

      refresh();
      return (data as { id: string } | null)?.id ?? null;
    },
    [refresh]
  );

  const updateArtifact = useCallback(
    async (
      artifactId: string,
      fields: Partial<Pick<DbArtifact, "name" | "description" | "sharing" | "team_id" | "registers_access_granted">>
    ): Promise<boolean> => {
      const { error } = await supabase
        .from("artifacts")
        .update(fields)
        .eq("id", artifactId);

      if (error) {
        console.error("[useArtifacts] update:", error);
        return false;
      }

      refresh();
      return true;
    },
    [refresh]
  );

  const deleteArtifact = useCallback(
    async (artifactId: string): Promise<boolean> => {
      const { error } = await supabase
        .from("artifacts")
        .delete()
        .eq("id", artifactId);

      if (error) {
        console.error("[useArtifacts] delete:", error);
        return false;
      }

      refresh();
      return true;
    },
    [refresh]
  );

  return { artifacts, loading, error, refresh, createArtifact, updateArtifact, deleteArtifact };
}
